import React from 'react'

import type { MascotSnout } from '../mascot-types'
import MascotEars, { type MascotEarAnchor } from './mascot-ears'
import MascotFur from './mascot-fur'
import {
    flatArtOutline,
    MascotFlatEye,
    MascotHeadShading,
    MascotLimbPair,
    MascotPatternLayer,
    MascotPoseFrame,
    MascotSvgRoot,
    MASCOT_PEEK_VIEWBOX,
    type MascotRegion,
    type MascotSvgProps,
} from './mascot-svg-shared'

/**
 * Dog, ported from the illustrated asset set.
 *
 * See `cat.tsx` for the porting conventions: the artwork stays on its
 * original 512 grid inside a `scale(0.25)` group, the asset's own ears are
 * dropped in favour of the shared swappable library, and every fixed hex is
 * remapped onto the coat variables so the clinic's colours drive the art.
 * Muzzle length is a setting on this skull, not a second species.
 */

const ART_SCALE = 0.25

const HEAD: MascotRegion = { cx: 64, cy: 54, rx: 48, ry: 45, eyeY: 53 }
/**
 * Eye placement, in portrait space. Taken from the centres of the asset's own
 * eye capsules, then drawn a little larger than the asset had them.
 */
const EYE = { y: 55.7, dx: 21.7, rx: 9.6, ry: 12.5 }
const EARS: MascotEarAnchor = {
    x: 34,
    y: 36,
    headCx: 64,
    scale: 1.35,
    rotate: 16,
    oneUpPair: ['pointed', 'folded'],
}
const PORTRAIT_VIEWBOX = '-14 -13 156 156'
const PEEK_TRANSFORM = 'translate(-12 -12) scale(1.18)'

/**
 * Skull outline, domed.
 *
 * The asset draws a flat top between the ear roots, because its own ears
 * covered that edge. Those ears are replaced by the swappable library, which
 * leaves the flat run and its square corners exposed — so the top is closed
 * over into a dome here. Everything from the temples down (the side points at
 * y=252.9 and the jaw) is the artist's original geometry.
 */
const HEAD_PATH = [
    'M63.312 252.904',
    'C63.312 148 148 40 256 40',
    'C364 40 448.689 148 448.689 252.904',
    'C441.807 334.452 391.226 378.839 351.312 395.011',
    'H160.689',
    'C120.774 378.839 70.194 334.452 63.312 252.904',
    'Z',
].join(' ')

/**
 * The muzzle's extent in portrait space, for the marking clip.
 *
 * Stated as its own ellipse rather than reusing {@link JOWL_PATH}: the art is
 * on the 512 grid and would need a transform inside the `clipPath`, which
 * isn't honoured consistently by renderers.
 */
const MUZZLE_REGION = { cx: 64, cy: 98, rx: 25, ry: 20 }

/**
 * The muzzle clip has to follow the snout. Left fixed, a shortened muzzle
 * leaves the clip covering bare background below it, and the head shading
 * paints a grey ghost of the muzzle that used to be there.
 */
function muzzleClipRegion(snout: MascotSnout): typeof MUZZLE_REGION {
    const { x, y, dy = 0 } = SNOUT_SCALE[snout]
    return {
        cx: MUZZLE_REGION.cx,
        cy: SNOUT_PIVOT.y + (MUZZLE_REGION.cy - SNOUT_PIVOT.y) * y + dy,
        rx: MUZZLE_REGION.rx * x,
        ry: MUZZLE_REGION.ry * y,
    }
}

/**
 * How each snout is proportioned, relative to the one this species was drawn
 * with. Scaling happens about {@link SNOUT_PIVOT} — the point where the
 * muzzle meets the face — so a shorter snout shrinks upward and ends up
 * sitting higher, which is what actually distinguishes a flat face from a
 * long one. Scaling about the shape's own centre just makes a small muzzle
 * floating in the same place.
 *
 * `long` is identity: it's the shape the artist drew. `flat` also lifts
 * (`dy`) so the nose sits nearer the eyes instead of remaining a squashed
 * pad on the chin.
 */
const SNOUT_PIVOT = { x: 64, y: 82 }
const SNOUT_SCALE: Record<MascotSnout, { x: number; y: number; dy?: number }> =
    {
        long: { x: 1, y: 1 },
        short: { x: 0.94, y: 0.78 },
        // Wider and less pancake-like than a pure squash, then shifted up so
        // the nose reads as mid-face (pug / bulldog) rather than a chin strap.
        flat: { x: 1.34, y: 0.88, dy: -16 },
    }

function snoutTransform(snout: MascotSnout): string {
    const { x, y, dy = 0 } = SNOUT_SCALE[snout]
    if (x === 1 && y === 1 && dy === 0) return ''
    const scale =
        x === 1 && y === 1
            ? ''
            : `translate(${SNOUT_PIVOT.x} ${SNOUT_PIVOT.y}) scale(${x} ${y}) translate(${-SNOUT_PIVOT.x} ${-SNOUT_PIVOT.y})`
    const lift = dy === 0 ? '' : `translate(0 ${dy})`
    return `${scale} ${lift}`.trim()
}

/**
 * Muzzle pad. Drawn twice on purpose: once under the markings so a mask or a
 * blaze can run across it, and once over the tongue so the tongue's flat root
 * stays hidden behind it.
 */
const MUZZLE_PAD_PATH =
    'M309.677 326.194H202.323C168.117 326.194 140.388 346.529 140.388 371.613C140.388 396.697 168.118 417.032 202.323 417.032C219.548 417.032 235.105 411.863 246.318 403.534C249.157 401.426 252.58 400.372 255.997 400.372C259.42 400.372 262.843 401.425 265.682 403.534C276.895 411.863 292.452 417.032 309.677 417.032C343.883 417.032 371.612 396.697 371.612 371.613C371.613 346.529 343.883 326.194 309.677 326.194Z'

/** Jowls. */
const JOWL_PATH =
    'M256 466.58C306.17 466.58 346.84 437.004 346.84 400.52C346.84 364.036 306.17 334.46 256 334.46C205.83 334.46 165.16 364.036 165.16 400.52C165.16 437.004 205.83 466.58 256 466.58Z'

export default function RegularDogMascot(props: MascotSvgProps) {
    const isPeek = props.pose === 'peek'
    const muzzleClip = muzzleClipRegion(props.snout)
    return (
        <MascotSvgRoot
            {...props}
            viewBox={isPeek ? MASCOT_PEEK_VIEWBOX : PORTRAIT_VIEWBOX}
        >
            {ids => (
                <MascotPoseFrame
                    pose={props.pose}
                    ids={ids}
                    peekTransform={PEEK_TRANSFORM}
                    paws={<MascotLimbPair kind="paw" />}
                >
                    <defs>
                        {/* Head region UNION muzzle. A clipPath unions its
                            children, and markings have to be able to reach the
                            muzzle — a mask or a blaze that stops at the skull
                            leaves the muzzle the wrong colour. */}
                        <clipPath id={ids.headClip}>
                            <ellipse
                                cx={HEAD.cx}
                                cy={HEAD.cy}
                                rx={HEAD.rx}
                                ry={HEAD.ry}
                            />
                            <ellipse
                                cx={muzzleClip.cx}
                                cy={muzzleClip.cy}
                                rx={muzzleClip.rx}
                                ry={muzzleClip.ry}
                            />
                        </clipPath>
                    </defs>

                    <MascotFur furType={props.furType} region={HEAD} flat />
                    <MascotEars style={props.earStyle} anchor={EARS} flat />

                    <g transform={`scale(${ART_SCALE})`}>
                        {/* Head */}
                        <path
                            d={HEAD_PATH}
                            fill="var(--coat-primary)"
                            {...flatArtOutline}
                        />
                    </g>
                    {/* Cheek shadow as translucent black, so it holds up on
                        dark coats as well as light ones. Trimmed, because the
                        asset drew it against the original flat-topped outline
                        and it spills past the domed edge. */}
                    <g clipPath={`url(#${ids.headClip})`}>
                        <g transform={`scale(${ART_SCALE})`}>
                            <path
                                d="M160.689 395.011H199.83C199.83 395.011 131.098 348.904 119.743 267.355C97.654 108.712 139.7 61.247 176.067 49.135C170.306 49.33 164.322 48.361 156.904 45.42H74.323L63.312 252.904C70.194 334.452 120.774 378.839 160.689 395.011Z"
                                fill="#000"
                                opacity={0.07}
                            />
                        </g>
                    </g>

                    <g transform={snoutTransform(props.snout)}>
                        <g transform={`scale(${ART_SCALE})`}>
                            {/* Jowls + muzzle. Painted before the markings so
                                a mask or a blaze can run across them. */}
                            <path d={JOWL_PATH} fill="var(--coat-secondary)" />
                            <path d={JOWL_PATH} fill="#000" opacity={0.12} />
                            <path
                                d={MUZZLE_PAD_PATH}
                                fill="var(--coat-secondary)"
                            />
                        </g>
                    </g>

                    <MascotPatternLayer
                        pattern={props.pattern}
                        clipPathId={ids.headClip}
                        region={HEAD}
                    />
                    <MascotHeadShading
                        clipPathId={ids.headClip}
                        region={HEAD}
                    />
                    <MascotEars
                        style={props.earStyle}
                        anchor={EARS}
                        flat
                        layer="front"
                    />

                    <g transform={snoutTransform(props.snout)}>
                        <g transform={`scale(${ART_SCALE})`}>
                            {/* Tongue */}
                            <path
                                d="M214.71 400.516V441.806C214.71 464.61 233.196 483.096 256 483.096C278.804 483.096 297.29 464.61 297.29 441.806V400.516H214.71Z"
                                fill="var(--coat-flesh)"
                            />
                            <path
                                d="M214.71 432.768C226.651 431.272 237.918 427.639 247.742 422.146V433.548C247.742 438.108 251.44 441.806 256 441.806C260.56 441.806 264.258 438.108 264.258 433.548V422.041C273.987 427.593 285.284 431.266 297.29 432.771V400.515H214.71V432.768Z"
                                fill="#000"
                                opacity={0.14}
                            />
                            <path
                                d={MUZZLE_PAD_PATH}
                                fill="var(--coat-secondary)"
                            />

                            {/* Nose */}
                            <path
                                d="M256 293.161C283.365 293.161 305.548 307.95 305.548 326.193C305.548 343.787 284.724 353.051 269.695 359.901C260.983 363.872 251.015 363.872 242.303 359.901C227.274 353.051 206.45 343.787 206.45 326.193C206.452 307.951 228.635 293.161 256 293.161Z"
                                fill="var(--coat-eye)"
                            />
                            <path
                                d="M230.968 317.161C229.813 306.768 237.134 295.654 237.134 295.654C219.131 300.603 206.452 312.405 206.452 326.193C206.452 343.786 227.276 353.05 242.305 359.901C251.016 363.871 260.985 363.871 269.697 359.901C272.929 358.428 276.429 356.832 279.932 355.077C259.613 351.484 233.29 338.065 230.968 317.161Z"
                                fill="#fff"
                                opacity={0.1}
                            />
                        </g>
                    </g>

                    <MascotFlatEye
                        cx={HEAD.cx - EYE.dx}
                        cy={EYE.y}
                        rx={EYE.rx}
                        ry={EYE.ry}
                    />
                    <MascotFlatEye
                        cx={HEAD.cx + EYE.dx}
                        cy={EYE.y}
                        rx={EYE.rx}
                        ry={EYE.ry}
                    />
                </MascotPoseFrame>
            )}
        </MascotSvgRoot>
    )
}
